/**
 * Generic SSE (Server-Sent Events) consumer for the Express backend
 *
 * Uses raw fetch() to call the API URL, parses SSE events
 * line-by-line, and calls the appropriate handler for each event.
 */

import * as Sentry from "@sentry/react";
import { SENTRY_TAGS, SENTRY_ERROR_TYPES } from "@/config/sentry";
import { getAccessToken } from "@/lib/api/authToken";

const API_URL = import.meta.env.VITE_API_URL;

interface SSEHandlers {
  onEvent: (event: string, data: unknown) => void;
  onError?: (error: string) => void;
}

/**
 * Invoke an API endpoint that returns an SSE stream.
 * Parses each SSE event and calls handlers.onEvent(eventName, parsedData).
 *
 * Pre-stream errors (4xx JSON responses) are detected and thrown.
 */
export async function invokeSSE(
  path: string,
  body: object,
  handlers: SSEHandlers
): Promise<void> {
  const accessToken = await getAccessToken();

  const url = `${API_URL}${path}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  // Pre-stream errors come back as regular JSON (not SSE)
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const message =
      errorData?.error || `Request failed with status ${response.status}`;
    const err = new Error(message);
    (err as Error & { code?: string }).code = errorData?.code;
    throw err;
  }

  if (!response.body) {
    throw new Error("No response body");
  }

  // Read the SSE stream line-by-line
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete lines
    const lines = buffer.split("\n");
    // Keep the last (possibly incomplete) line in the buffer
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith("data: ") && currentEvent) {
        try {
          const data = JSON.parse(line.slice(6));
          if (currentEvent === "error" && handlers.onError) {
            handlers.onError(
              typeof data.error === "string" ? data.error : JSON.stringify(data)
            );
          } else {
            handlers.onEvent(currentEvent, data);
          }
        } catch (err) {
          console.error("Failed to parse SSE data:", line);
          // Malformed SSE payload — report so silent stream corruption is
          // visible. The raw line is omitted (it may carry user content).
          Sentry.withScope((scope) => {
            scope.setTag(SENTRY_TAGS.TYPE, SENTRY_ERROR_TYPES.SSE_PARSE_ERROR);
            scope.setExtra("path", path);
            scope.setLevel("warning");
            Sentry.captureException(
              err instanceof Error ? err : new Error("Failed to parse SSE data"),
            );
          });
        }
        currentEvent = "";
      }
      // Empty lines (event separators) — no action needed
    }
  }
}
